import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import DeviceModal from '../components/DeviceModal';
import ConfirmModal from '../components/ConfirmModal';
import DeviceCard from '../components/DeviceCard';
import { Device, DeviceCreate, DeviceUpdate } from '../types';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

interface DashboardDevice {
  id: number;
  name: string;
  model: string;
  balance: number;
  bank_name: string;
  card_number: string;
  card_usage_count: number;
}

interface Statistics {
  total_balance: number;
  total_devices: number;
  today_income: number;
  monthly_income: number;
}

const Devices: React.FC = () => {
  const [dashboardDevices, setDashboardDevices] = useState<DashboardDevice[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [confirmDeleteName, setConfirmDeleteName] = useState<string>('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    loadDashboardDevices();
    loadStatistics();
  }, []);

  const loadDashboardDevices = async () => {
    try {
      setLoading(true);
      const data = await apiService.getDevices();
      setDashboardDevices(data);
    } catch (err) {
      console.error('Error loading dashboard devices:', err);
      toast.error('Не удалось загрузить устройства');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await apiService.getStatisticsOverview();
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const handleAddDevice = () => {
    setModalMode('add');
    setSelectedDevice(null);
    setIsModalOpen(true);
  };

  const handleEditDevice = (device: Device) => {
    setModalMode('edit');
    setSelectedDevice(device);
    setIsModalOpen(true);
  };

  const handleDeleteDevice = (deviceId: number, deviceName: string) => {
    setConfirmDeleteId(deviceId);
    setConfirmDeleteName(deviceName);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setDeleteLoading(confirmDeleteId);
      await apiService.deleteDevice(confirmDeleteId);
      await loadDashboardDevices();
      await loadStatistics();
      setConfirmDeleteId(null);
      setConfirmDeleteName('');
    } catch (err) {
      console.error('Error deleting device:', err);
      alert('Ошибка при удалении устройства');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteId(null);
    setConfirmDeleteName('');
  };

  const handleSaveDevice = async (deviceData: DeviceCreate | DeviceUpdate) => {
    try {
      if (modalMode === 'add') {
        await apiService.createDevice(deviceData);
      } else if (selectedDevice) {
        await apiService.updateDevice(selectedDevice.id, deviceData);
      }
      
      await loadDashboardDevices();
      await loadStatistics();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving device:', err);
      throw err;
    }
  };

  const handleDragEnd = async (event: any) => {
    const {active, over} = event;
    if (active.id !== over?.id) {
      const oldIndex = dashboardDevices.findIndex(d => d.id === active.id);
      const newIndex = dashboardDevices.findIndex(d => d.id === over.id);
      const newDevices = arrayMove(dashboardDevices, oldIndex, newIndex);
      setDashboardDevices(newDevices);
      // Обновляем порядок на сервере
      await apiService.reorderDevices(newDevices.map((d, idx) => ({id: d.id, order: idx})));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="px-8 py-8 bg-[#181A20] min-h-screen">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white tracking-wide">Устройства</h1>
      </div>

      {/* Количество устройств */}
      {statistics && (
        <div className="mb-8">
          <DeviceCard 
            title="Всего устройств"
            value={statistics.total_devices}
            className="w-full max-w-xs"
            onAddClick={handleAddDevice}
          />
        </div>
      )}

      {/* Табличный вид для десктопа */}
      <div className="bg-[#181A20] rounded-xl shadow border border-[#23262F] hidden md:block">
        <div className="px-6 py-4 border-b border-[#23262F]">
          <h2 className="text-xl font-semibold text-white">Список устройств</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#23262F]">
            <thead className="bg-[#181A20]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">№</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">Имя устройства</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">Модель</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">Номер карты</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">Баланс</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#B1B5C3] uppercase tracking-wider">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-[#181A20] divide-y divide-[#23262F]">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={dashboardDevices.map(d => d.id)} strategy={verticalListSortingStrategy}>
                  {dashboardDevices.map((device, index) => (
                    <SortableRow
                      key={device.id}
                      device={device}
                      index={index}
                      onEdit={handleEditDevice}
                      onDelete={handleDeleteDevice}
                      deleteLoading={deleteLoading}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </tbody>
          </table>
        </div>
        {dashboardDevices.length === 0 && (
          <div className="px-6 py-8 text-center text-[#B1B5C3]">
            Устройства не найдены. Добавьте первое устройство!
          </div>
        )}
      </div>

      {/* Карточный вид для мобильных */}
      <div className="bg-[#181A20] rounded-xl shadow border border-[#23262F] md:hidden">
        <div className="px-4 py-3 border-b border-[#23262F]">
          <h2 className="text-lg font-semibold text-white">Список устройств</h2>
        </div>
        <div className="divide-y divide-[#23262F]">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={dashboardDevices.map(d => d.id)} strategy={verticalListSortingStrategy}>
              {dashboardDevices.map((device, index) => (
                <MobileDeviceCard
                  key={device.id}
                  device={device}
                  index={index}
                  onEdit={handleEditDevice}
                  onDelete={handleDeleteDevice}
                  deleteLoading={deleteLoading}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
        {dashboardDevices.length === 0 && (
          <div className="px-4 py-6 text-center text-[#B1B5C3]">
            Устройства не найдены. Добавьте первое устройство!
          </div>
        )}
      </div>

      {/* Модальное окно для добавления/редактирования устройства */}
      <DeviceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        device={selectedDevice}
        onSave={handleSaveDevice}
        mode={modalMode}
      />

      {/* Модальное окно подтверждения удаления */}
      <ConfirmModal
        isOpen={!!confirmDeleteId}
        title="Удаление устройства"
        message={`Вы действительно хотите удалить устройство "${confirmDeleteName}"? Это действие необратимо.`}
        confirmText="Удалить"
        cancelText="Отмена"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={deleteLoading === confirmDeleteId}
      />
    </div>
  );
};

function DraggableRow({device, index, listeners, attributes, setNodeRef, style, ...props}: any) {
  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-[#23262F] transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          <div 
            {...attributes} 
            {...listeners}
            className="cursor-move p-1 hover:bg-[#353945] rounded transition-colors"
            title="Перетащите для изменения порядка"
          >
            <svg className="w-4 h-4 text-[#B1B5C3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h2M4 10h2M4 14h2M4 18h2M10 6h2M10 10h2M10 14h2M10 18h2M16 6h2M16 10h2M16 14h2M16 18h2" />
            </svg>
          </div>
          <div className="text-sm font-medium text-[#B1B5C3]">{index + 1}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-white">{device.name}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-[#B1B5C3]">{device.model || 'Не указана'}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-[#B1B5C3]">
          {device.card_number ? (
            <div>
              <div>{device.card_number}</div>
              <div className="text-xs mt-1 text-green-400">
                {device.card_usage_count || 0} из 3
              </div>
            </div>
          ) : (
            'Не указан'
          )}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-green-400">{Number(device.balance || 0).toFixed(2)} ₽</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex space-x-2">
          <button
            onClick={() => props.onEdit(device)}
            className="text-blue-400 hover:text-blue-300 transition-colors p-1"
            title="Редактировать"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => props.onDelete(device.id, device.name)}
            disabled={props.deleteLoading === device.id}
            className="text-red-400 hover:text-red-300 transition-colors p-1 disabled:opacity-50"
            title="Удалить"
          >
            {props.deleteLoading === device.id ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-400"></div>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      </td>
    </tr>
  );
}

function SortableRow(props: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({id: props.device.id});
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#23262F' : undefined
  };
  return <DraggableRow {...props} setNodeRef={setNodeRef} listeners={listeners} attributes={attributes} style={style} />;
}

export default Devices; 

function MobileDeviceCard(props: any) {
  const { device, index, onEdit, onDelete, deleteLoading } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({id: device.id});
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: isDragging ? '#23262F' : undefined
  } as React.CSSProperties;

  return (
    <div ref={setNodeRef} style={style} className="px-4 py-3">
      <div className="flex items-start gap-3">
        <div
          {...attributes}
          {...listeners}
          className="cursor-move p-1 mt-0.5 hover:bg-[#353945] rounded transition-colors"
          title="Перетащите для изменения порядка"
        >
          <svg className="w-4 h-4 text-[#B1B5C3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h2M4 10h2M4 14h2M4 18h2M10 6h2M10 10h2M10 14h2M10 18h2M16 6h2M16 10h2M16 14h2M16 18h2" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-white text-sm font-semibold truncate">
              {index + 1}. {device.name}
            </div>
            <div className="text-sm font-medium text-green-400 whitespace-nowrap">{device.balance.toFixed(2)} ₽</div>
          </div>
          <div className="text-xs text-[#B1B5C3] mt-1 truncate">{device.model || 'Не указана'}</div>
          <div className="text-xs text-[#B1B5C3] mt-1">
            {device.card_number ? (
              <div className="flex items-center justify-between gap-3">
                <span className="truncate">{device.card_number}</span>
                <span className="text-[11px] text-green-400 whitespace-nowrap">{device.card_usage_count || 0} из 3</span>
              </div>
            ) : (
              'Карта: не указана'
            )}
          </div>
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={() => onEdit(device)}
              className="flex-1 text-center text-blue-400 hover:text-blue-300 transition-colors py-1.5 bg-[#1B1D24] rounded"
            >
              Редактировать
            </button>
            <button
              onClick={() => onDelete(device.id, device.name)}
              disabled={deleteLoading === device.id}
              className="flex-1 text-center text-red-400 hover:text-red-300 transition-colors py-1.5 bg-[#1B1D24] rounded disabled:opacity-50"
            >
              {deleteLoading === device.id ? 'Удаление…' : 'Удалить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}